import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { CoinIcon, FloatIcon, RubyIcon } from './CurrencyIcons';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { celebrate, haptic } from '../utils/fx';
import { useTelegramMainButton } from '../hooks/useTelegramChrome';
import { mergeClickerState } from '../utils/clickerMerge';
import { awaitDrainPendingTaps } from '../utils/tapFlush';

const ONBOARDING = [
  { step: 0, icon: 'button.png', titleKey: 'obTapTitle', bodyKey: 'obTapBody', path: '/clicker' },
  { step: 1, icon: 'toolbox.png', titleKey: 'obBuildTitle', bodyKey: 'obBuildBody', path: '/clicker/upgrade' },
  { step: 2, icon: 'crown.png', titleKey: 'obPassTitle', bodyKey: 'obPassBody', path: '/clicker/earn' },
];

/**
 * Takes the two slices it reads instead of the whole clicker state: energy ticks
 * rewrite `clickerData` several times a second and this layer has nothing to do
 * with energy, so memo + narrow props keep it out of the tap path entirely.
 */
const EngagementLayer = memo(function EngagementLayer({
  engagement: eng,
  offline: offlineProp,
  setClickerData,
}) {
  const { t, lang } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/clicker' || location.pathname === '/clicker/';

  // Explicit null from server/claim must win — never fall back to a stale nested copy
  const offline = offlineProp !== undefined ? offlineProp : eng?.offline;
  const calendar = eng?.calendar;
  const offers = useMemo(() => eng?.offers || [], [eng?.offers]);
  const event = eng?.event;
  const onboardingDone = eng?.onboarding_done;
  const onboardingStep = eng?.onboarding_step ?? 0;

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const busyRef = useRef(false);
  const snoozeDaily = useRef(false);
  /** Stable per offline window (not growing earned) so sheet opens once. */
  const offlineWindowKey = useRef(null);
  /** Frozen amount shown in the sheet for this window. */
  const offlineFrozenEarned = useRef(null);
  const offlineDismissed = useRef(false);

  const refresh = useCallback(async () => {
    const drained = await awaitDrainPendingTaps({ timeoutMs: 12000 });
    if (drained.last) {
      setClickerData?.((prev) =>
        mergeClickerState(prev, drained.last, { preferRemoteBalance: true })
      );
    }
    const data = await api.clickerSync();
    setClickerData?.((prev) =>
      mergeClickerState(prev, data, { preferRemoteBalance: true })
    );
    return data;
  }, [setClickerData]);

  useEffect(() => {
    if (calendar?.can_claim && isHome && !snoozeDaily.current) {
      setCalendarOpen(true);
    }
  }, [calendar?.can_claim, isHome]);

  useEffect(() => {
    if (!isHome) {
      setOfflineOpen(false);
      setCalendarOpen(false);
    }
  }, [isHome]);

  // Offline claim: open sheet once per offline window when on home.
  // Key by last_passive_at — NOT by growing earned (that reopened forever).
  useEffect(() => {
    const earned = offline?.earned;
    if (!earned) {
      offlineWindowKey.current = null;
      offlineFrozenEarned.current = null;
      offlineDismissed.current = false;
      setOfflineOpen(false);
      return;
    }
    if (!isHome) return;
    const windowKey =
      String(offline.last_passive_at || offline.since || offline.seconds || '') || 'pending';
    if (offlineWindowKey.current !== windowKey) {
      offlineWindowKey.current = windowKey;
      offlineFrozenEarned.current = earned;
      offlineDismissed.current = false;
      setOfflineOpen(true);
    }
  }, [isHome, offline?.earned, offline?.last_passive_at, offline?.since, offline?.seconds]);

  const withBusy = useCallback(async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setErrorMsg('');
    try {
      await fn();
    } catch (err) {
      haptic('error');
      setErrorMsg(err?.message || t('bootError'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [t]);

  const claimDaily = useCallback(async () => {
    await withBusy(async () => {
      const drained = await awaitDrainPendingTaps();
      if (drained.last) {
        setClickerData?.((prev) =>
          mergeClickerState(prev, drained.last, { preferRemoteBalance: true })
        );
      }
      const res = await api.claimDaily();
      celebrate('level');
      setClickerData?.((prev) =>
        mergeClickerState(prev, res.clicker || {}, {
          preferRemoteBalance: true,
          engagement: {
            ...(prev?.engagement || {}),
            ...(res.clicker?.engagement || {}),
            calendar: res.calendar || {
              ...(prev?.engagement?.calendar || {}),
              can_claim: false,
              claimed_today: true,
            },
          },
        })
      );
      setCalendarOpen(false);
      snoozeDaily.current = false;
    });
  }, [withBusy, setClickerData]);

  const claimOffline = useCallback(async () => {
    await withBusy(async () => {
      const drained = await awaitDrainPendingTaps();
      if (drained.last) {
        setClickerData?.((prev) =>
          mergeClickerState(prev, drained.last, { preferRemoteBalance: true })
        );
      }
      const res = await api.claimOffline();
      celebrate('league');
      setClickerData?.((prev) =>
        mergeClickerState(prev, res.clicker || {}, {
          preferRemoteBalance: true,
          offline: null,
          engagement: {
            ...(prev?.engagement || {}),
            ...(res.clicker?.engagement || {}),
            offline: null,
          },
        })
      );
      setOfflineOpen(false);
      offlineWindowKey.current = null;
      offlineFrozenEarned.current = null;
      offlineDismissed.current = false;
    });
  }, [withBusy, setClickerData]);

  const claimOffer = useCallback(
    async (code) => {
      await withBusy(async () => {
        await api.claimOffer(code);
        celebrate('level');
        await refresh();
      });
    },
    [withBusy, refresh]
  );

  /** Snappy onboarding: optimistic UI first, API in background — no full sync wait */
  const finishOnboarding = useCallback(
    ({ done = false, step = null, path = null } = {}) => {
      setClickerData?.((prev) => {
        if (!prev) return prev;
        const engPrev = prev.engagement || {};
        return {
          ...prev,
          engagement: {
            ...engPrev,
            onboarding_done: done ? true : Boolean(engPrev.onboarding_done),
            onboarding_step: done ? engPrev.onboarding_step ?? 0 : step ?? engPrev.onboarding_step ?? 0,
          },
        };
      });
      if (path) navigate(path);
      api
        .setOnboarding(done ? { done: true } : { step })
        .catch(() => {
          /* keep optimistic UI */
        });
    },
    [navigate, setClickerData]
  );

  const mainClaimLabel = useMemo(() => {
    if (!isHome || !onboardingDone) return null;
    if (calendar?.can_claim && calendarOpen) return t('claimDailyBtn');
    // Offline: MainButton only while sheet is open (claim), not as a permanent banner
    if (offline?.earned && offlineOpen) return t('claimOfflineBtn');
    if (calendar?.can_claim) return t('claimDailyBtn');
    if (offers[0]) return lang === 'en' ? offers[0].title_en || offers[0].title : offers[0].title;
    return null;
  }, [isHome, onboardingDone, offline, calendar, offers, t, lang, calendarOpen, offlineOpen]);

  const onMain = useCallback(() => {
    if (calendar?.can_claim && calendarOpen) claimDaily();
    else if (offline?.earned && offlineOpen) claimOffline();
    else if (calendar?.can_claim) {
      setCalendarOpen(true);
    } else if (offers[0]) claimOffer(offers[0].code);
  }, [calendar, calendarOpen, offline, offlineOpen, offers, claimDaily, claimOffline, claimOffer]);

  useTelegramMainButton({
    show: Boolean(mainClaimLabel),
    label: mainClaimLabel || '',
    onClick: onMain,
  });

  const showOnboarding = eng && !onboardingDone;
  const stepMeta = ONBOARDING[Math.min(onboardingStep, 2)];

  const dayDone = (d) => {
    if (!calendar) return false;
    if (calendar.claimed_today) return d.day <= calendar.streak_day;
    if (calendar.can_claim && calendar.next_day > 1) return d.day < calendar.next_day;
    return false;
  };

  // Пока баннер события висит над главной, содержимое опускаем: он
  // позиционирован фиксированно сразу под шапкой и иначе ложится поверх
  // крупного баланса.
  useEffect(() => {
    const on = Boolean(isHome && event);
    document.body.classList.toggle('has-event-banner', on);
    return () => document.body.classList.remove('has-event-banner');
  }, [isHome, event]);

  return (
    <>
      {isHome && event ? (
        <div className="event-banner glass-panel soft-card">
          <FloatIcon src={iconUrl('fire.png')} size={20} eager native animated />
          <span>
            {lang === 'en' ? event.title_en || event.title : event.title}
            <em>
              {' '}
              ×{event.bp_xp_mult}
            </em>
          </span>
        </div>
      ) : null}

      {isHome && offlineOpen && offline?.earned ? (
        <div className="offline-sheet is-open" role="dialog" aria-modal="true">
          <button
            type="button"
            className="daily-backdrop"
            aria-label={t('close')}
            onClick={() => {
              offlineDismissed.current = true;
              setOfflineOpen(false);
            }}
          />
          <div className="offline-sheet-inner glass-panel soft-card">
            <FloatIcon src={iconUrl('chart_up.png')} size={72} eager native animated />
            <div className="upgrades-item-info-name">{t('offlineTitle')}</div>
            <div className="offline-earn-amount">
              <CoinIcon size={28} /> +{offlineFrozenEarned.current || offline.earned}
            </div>
            <div className="item-desc">
              {t('offlineBody', { n: offlineFrozenEarned.current || offline.earned })}
            </div>
            {errorMsg ? <div className="inline-message">{errorMsg}</div> : null}
            <button type="button" className="action-btn soft-btn offline-claim-big" disabled={busy} onClick={claimOffline}>
              <CoinIcon size={18} /> {t('claimOfflineBtn')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Compact reminder only if user dismissed the sheet without claiming */}
      {isHome && offline?.earned && !offlineOpen && offlineDismissed.current ? (
        <div className="offline-claim glass-panel soft-card">
          <div className="offline-claim-text">
            <strong>{t('offlineTitle')}</strong>
            <span>{t('offlineBody', { n: offlineFrozenEarned.current || offline.earned })}</span>
          </div>
          <button
            type="button"
            className="action-btn soft-btn"
            disabled={busy}
            onClick={() => {
              offlineDismissed.current = false;
              setOfflineOpen(true);
            }}
          >
            <CoinIcon size={14} /> {t('claimOfflineBtn')}
          </button>
        </div>
      ) : null}

      {isHome &&
        offers.map((o) => (
          <div className="offer-card glass-panel soft-card" key={o.code}>
            <div className="item-text">
              <span className="upgrades-item-info-name">
                {lang === 'en' ? o.title_en || o.title : o.title}
              </span>
              <span className="item-desc reward-line">
                <CoinIcon size={14} /> +{o.coins}
                {o.amber ? (
                  <>
                    {' '}
                    <RubyIcon size={14} /> +{o.amber}
                  </>
                ) : null}
              </span>
            </div>
            <button type="button" className="action-btn soft-btn" disabled={busy} onClick={() => claimOffer(o.code)}>
              {t('claim')}
            </button>
          </div>
        ))}

      {isHome && calendarOpen && calendar ? (
        <div className={`daily-sheet ${calendarOpen ? 'is-open' : ''}`}>
          <button
            type="button"
            className="daily-backdrop"
            aria-label={t('close')}
            onClick={() => {
              snoozeDaily.current = true;
              setCalendarOpen(false);
            }}
          />
          <div className="daily-sheet-inner glass-panel soft-card">
            <div className="upgrades-item-info-name">{t('dailyTitle')}</div>
            <div className="item-desc">{t('dailySubtitle')}</div>
            <div className="daily-grid">
              {(calendar.days || []).map((d) => (
                <div
                  key={d.day}
                  className={`daily-cell ${dayDone(d) ? 'is-done' : ''} ${
                    calendar.can_claim && d.day === calendar.next_day ? 'is-next' : ''
                  }`}
                >
                  <span className="daily-day">{d.day}</span>
                  <span className="reward-line">
                    <CoinIcon size={12} /> {d.coins}
                  </span>
                </div>
              ))}
            </div>
            {errorMsg ? <div className="inline-message">{errorMsg}</div> : null}
            <button type="button" className="action-btn soft-btn" disabled={busy || !calendar.can_claim} onClick={claimDaily}>
              {t('claimDailyBtn')}
            </button>
          </div>
        </div>
      ) : null}

      {isHome && calendar?.can_claim && !calendarOpen ? (
        <button
          type="button"
          className="daily-fab is-claim soft-btn"
          onClick={() => {
            snoozeDaily.current = false;
            setCalendarOpen(true);
          }}
          aria-label={t('claimDailyBtn')}
        >
          <FloatIcon src={iconUrl('chart_up.png')} size={22} eager native />
          <span>!</span>
        </button>
      ) : null}

      {showOnboarding && stepMeta ? (
        <div className="onboarding-overlay">
          <div className="onboarding-card glass-panel soft-card">
            <FloatIcon src={iconUrl(stepMeta.icon)} size={48} eager native animated />
            <div className="upgrades-item-info-name">{t(stepMeta.titleKey)}</div>
            <div className="item-desc">{t(stepMeta.bodyKey)}</div>
            {errorMsg ? <div className="inline-message">{errorMsg}</div> : null}
            <div className="onboarding-dots">
              {ONBOARDING.map((s) => (
                <i key={s.step} className={s.step === onboardingStep ? 'is-on' : ''} />
              ))}
            </div>
            <div className="invite-actions">
              <button
                type="button"
                className="action-btn stars soft-btn"
                onClick={() => finishOnboarding({ done: true, path: '/clicker' })}
              >
                {t('obSkip')}
              </button>
              <button
                type="button"
                className="action-btn soft-btn"
                onClick={() => {
                  const next = onboardingStep + 1;
                  if (next >= 3) {
                    finishOnboarding({ done: true, path: '/clicker' });
                  } else {
                    finishOnboarding({ step: next, path: ONBOARDING[next].path });
                  }
                }}
              >
                {t('obNext')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
});

export default EngagementLayer;
